import { ChangeDetectionStrategy, Component, type OnInit, inject } from '@angular/core';
import { type Observable, Subject } from 'rxjs';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProductCatalogFacade } from '@web/store/website/product-catalog/product-catalog.facade';
import { type CatalogCategory, type CatalogProductCard } from '@web/widgets/product-card/type/product-card-widget.type';
import { debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ProductCatalogHttpService } from '@web/store/website/product-catalog/product-catalog.http.service';
import { type ProductCard } from '@common/types/product-card.type';
import { FormBuilder, FormControl, type FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProductCardService } from '@web/widgets/product-card/service/product-card.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CatalogCardComponent } from './catalog-card/catalog-card.component';
import { CommonModule } from '@angular/common';

const PER_PAGE_ELEMENTS = 20;

@Component({
    selector: 'web-catalog-modal',
    templateUrl: './catalog-modal.component.html',
    styleUrls: ['./catalog-modal.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, CommonModule, ReactiveFormsModule, CatalogCardComponent, TranslateModule],
})
export class CatalogModalComponent implements OnInit {
    private readonly bsModalRef = inject(BsModalRef);
    private readonly productCatalogFacade = inject(ProductCatalogFacade);
    private readonly productCatalogHttpService = inject(ProductCatalogHttpService);
    private readonly fb = inject(FormBuilder);
    private readonly productCardService = inject(ProductCardService);
    private readonly translate = inject(TranslateService);

    public onClose: Subject<ProductCard>;

    public readonly cards$: Observable<CatalogProductCard[]> = this.productCatalogFacade.products$;
    public readonly categories$: Observable<CatalogCategory[]> = this.productCatalogFacade.categories$;
    public flattenedCategories$: Observable<CatalogCategory[]> = this.categories$.pipe(
        map((categories) => this.getFlattenedCategories(categories))
    );
    public readonly loading$: Observable<boolean> = this.productCatalogFacade.isLoading$;
    public readonly selectedCategory$: Observable<number> = this.productCatalogFacade.selectedCategory$;
    public readonly total$: Observable<number> = this.productCatalogFacade.total$;

    public catalogForm: FormGroup<{ search: FormControl<string>; offset: FormControl<number> }>;

    public ngOnInit(): void {
        this.onClose = new Subject();
        this.productCatalogFacade.loadCatalogContent();
        this.initForm();
        this.initSubscription();
    }

    public onConfirm(productId: number): void {
        this.productCatalogHttpService
            .getProductById(productId)
            .pipe(
                switchMap((catalogProduct) => {
                    return this.productCardService.transformProductData(catalogProduct);
                })
            )
            .subscribe((productData) => {
                this.onClose.next(productData);
                this.bsModalRef.hide();
            });
    }

    public onCancel(): void {
        this.onClose.next(null);
        this.bsModalRef.hide();
    }

    public setCategory(category: number) {
        this.productCatalogFacade.setSelectedCategory(category);
        this.searchControl.patchValue(null);
    }

    private loadProductsWithFilter(categoryId: number, search: string = '', offset: number = 0) {
        this.productCatalogFacade.loadProductsWithFilter(categoryId, search, offset);
    }

    public paginationHandler(increment: boolean = false) {
        const value = this.offsetControl.value;

        this.offsetControl.patchValue(increment ? value + PER_PAGE_ELEMENTS : value - PER_PAGE_ELEMENTS);
    }

    private getFlattenedCategories(categories: CatalogCategory[], depth = 0): CatalogCategory[] {
        let flattened: CatalogCategory[] = [];

        categories.forEach((category) => {
            flattened.push({
                ...category,
                name: '-'.repeat(depth) + ' ' + this.translate.instant(category.name),
            });
            if (category.children) {
                flattened = flattened.concat(this.getFlattenedCategories(category.children, depth + 1));
            }
        });

        return flattened;
    }

    private initForm() {
        this.catalogForm = this.fb.group({
            search: new FormControl(''),
            offset: new FormControl(0),
        });
    }

    private initSubscription() {
        this.searchControl.valueChanges
            .pipe(debounceTime(500), withLatestFrom(this.selectedCategory$))
            .subscribe(([search, categoryId]) => {
                this.loadProductsWithFilter(categoryId, search);
            });

        this.offsetControl.valueChanges
            .pipe(withLatestFrom(this.selectedCategory$))
            .subscribe(([offset, categoryId]) => {
                this.loadProductsWithFilter(categoryId, this.searchControl.value, offset);
            });
    }

    public get searchControl(): FormControl<string> {
        return this.catalogForm.get('search') as FormControl<string>;
    }

    public get offsetControl(): FormControl<number> {
        return this.catalogForm.get('offset') as FormControl<number>;
    }

    protected readonly PER_PAGE_ELEMENTS = PER_PAGE_ELEMENTS;
}
